/**
 * "Save as test scenario" (Replay Lab Phase 1b) — captures this Inbox
 * conversation via POST /api/conversations/{id}/save-as-scenario.
 * Anonymization (name/phone/known-address → placeholders) happens at
 * capture time on the backend; the checkbox maps to the `anonymize` flag.
 * Mount conditionally ({show && <SaveScenarioModal open …/>}) so the name
 * field re-initializes each time it opens.
 */
import { useState } from 'react'

import {
  Button,
  Input,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Spinner,
  useToast,
} from '@/components/ui'
import { useSaveScenario } from '@/hooks/useReplayLab'

interface SaveScenarioModalProps {
  conversationId: string
  defaultName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SaveScenarioModal({
  conversationId,
  defaultName,
  open,
  onOpenChange,
}: SaveScenarioModalProps) {
  const [name, setName] = useState(defaultName)
  const [anonymize, setAnonymize] = useState(true)
  const save = useSaveScenario()
  const { addToast } = useToast()

  const handleSave = () => {
    if (!name.trim() || save.isPending) return
    save.mutate(
      { conversationId, name: name.trim(), anonymize },
      {
        onSuccess: () => {
          addToast({
            type: 'success',
            title: 'Scenario saved',
            description: 'Find it under Replay Lab.',
          })
          onOpenChange(false)
        },
      },
    )
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="sm">
        <ModalHeader>
          <ModalTitle>Save as test scenario</ModalTitle>
          <ModalDescription>
            Captures the customer turns and final order state so Replay Lab
            can replay them against the current engine.
          </ModalDescription>
        </ModalHeader>
        <div className="space-y-3 py-2">
          <div>
            <label
              htmlFor="scenario-name"
              className="block text-xs font-medium text-foreground mb-1"
            >
              Scenario name
            </label>
            <Input
              id="scenario-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
            />
          </div>
          <label className="flex items-start gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={anonymize}
              onChange={(e) => setAnonymize(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Anonymize customer data
              <span className="block text-xs text-muted-foreground">
                Replaces the customer's name, phone, and known address with
                placeholders before anything is stored.
              </span>
            </span>
          </label>
          {save.isError && (
            <p role="alert" className="text-xs text-destructive">
              Couldn't save the scenario — {save.error.message}
            </p>
          )}
        </div>
        <ModalFooter>
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!name.trim() || save.isPending}>
            {save.isPending && <Spinner size="sm" className="mr-1" />}
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
